/**
 * Date, currency, token count, and byte size formatters.
 * All functions are pure — no side effects, no React imports.
 */

/**
 * Format an ISO date string for display.
 * Returns 'Invalid date' for unparseable input.
 */
export function formatDate(
  iso: string,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Invalid date'
  return new Intl.DateTimeFormat('en-US', opts).format(d)
}

/**
 * Format an ISO date string including time-of-day for display.
 * Returns 'Invalid date' for unparseable input.
 * Example output: "Jan 15, 2024, 10:30 AM"
 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Invalid date'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

/**
 * Format an ISO date string for run history display (no year).
 * Returns '—' for unparseable input.
 * Example output: "Nov 3, 9:31 AM"
 */
export function formatRunDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

/**
 * Format an ISO date string including time-of-day and timezone abbreviation.
 * Returns 'Invalid date' for unparseable input.
 * Example output: "Jan 15, 2024, 10:30 AM PST"
 */
export function formatDateTimeWithTZ(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Invalid date'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d)
}

/**
 * Convert a snake_case node name to a human-readable title-cased string.
 * Example: "resolve_client" → "Resolve Client"
 */
export function humanizeNodeName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Format a numeric amount as a currency string.
 * Defaults to USD with 2 decimal places.
 */
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format a token count with K / M suffixes.
 * Rounds to 1 decimal place; trailing '.0' is omitted (e.g. '2K tokens', not '2.0K tokens').
 */
export function formatTokens(n: number): string {
  if (n >= 1_000_000) {
    const v = parseFloat((n / 1_000_000).toFixed(1))
    return `${v}M tokens`
  }
  if (n >= 1_000) {
    const v = parseFloat((n / 1_000).toFixed(1))
    return `${v}K tokens`
  }
  return `${n} tokens`
}

/** Convert GA4 date string YYYYMMDD to JS Date */
export function parseGa4Date(d: string): Date {
  return new Date(`${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`)
}

/**
 * Format a byte count with KB / MB / GB suffixes (binary: 1024-based).
 * Rounds to 1 decimal place; trailing '.0' is omitted.
 */
export function formatBytes(n: number): string {
  if (n >= 1_073_741_824) {
    const v = parseFloat((n / 1_073_741_824).toFixed(1))
    return `${v} GB`
  }
  if (n >= 1_048_576) {
    const v = parseFloat((n / 1_048_576).toFixed(1))
    return `${v} MB`
  }
  if (n >= 1_024) {
    const v = parseFloat((n / 1_024).toFixed(1))
    return `${v} KB`
  }
  return `${n} B`
}
